import { isArchivedSessionStatus, isLiveSession } from '#modules/finder/features/posts/model/sessionStatus';
import { ROOM_APPEARS_BEFORE_START_MS } from '#features/voice/model/voice';

/**
 * Минимум, по которому решается «эта сессия сейчас моя». Всё остальное – содержимое комнаты, а не
 * её причина показаться, поэтому в правило не входит.
 *
 * Проверки «а принят ли я в состав» здесь нет: `mySessions` отдаёт сессии по участию со статусом
 * ACCEPTED, то есть отбор уже сделал сервер. Повторять его на клиенте по неполному списку
 * участников значило бы иногда прятать комнату у того, кто в сессии есть.
 */
export type CurrentSessionCandidate = {
  sessionId: string;
  /** Время старта и конца, как их отдаёт бэкенд, – ISO-строкой. */
  startedAt: string | null;
  expiredAt: string | null;
  /** UPCOMING | LIVE | COMPLETED | CANCELLED. */
  status: string | null;
};

type PickOptions = {
  /**
   * Время передаётся снаружи, а не берётся `Date.now()` внутри: правило целиком про «сейчас», и
   * иначе его нельзя ни протестировать, ни пересчитать по таймеру на границе фазы.
   */
  now: number;
  /** Сессия, в чьём голосе я прямо сейчас сижу, если такая есть. */
  voiceSessionId?: string | null;
};

const timeOf = (iso: string | null | undefined): number =>
  iso ? new Date(iso).getTime() : Number.NaN;

const earliest = <T extends CurrentSessionCandidate>(candidates: T[]): T | null =>
  candidates.reduce<T | null>(
    (best, candidate) =>
      best && timeOf(best.startedAt) <= timeOf(candidate.startedAt) ? best : candidate,
    null
  );

/**
 * Одна сессия, которую показывает комната. Переключателя между сессиями нет – значит выбор обязан
 * быть предсказуемым, а не «первая из списка»: порядок ответа сервера человеку не виден.
 *
 * Приоритет: (1) та, в чьём голосе я сижу – я уже разговариваю, и подменить её другой значит
 * убрать с экрана микрофон и состав тех, кого я слышу; (2) самая ранняя идущая; (3) ближайшая,
 * стартующая в окне `ROOM_APPEARS_BEFORE_START_MS` – в тот же момент открывается голос, и раньше
 * комнате нечего предложить.
 *
 * Голос проверяется ДО временнóго отбора, а не среди прошедших его кандидатов: «я сижу в этом
 * разговоре» – факт, а окно T−10 и конец сессии считаются по часам браузера, которые расходятся
 * с серверными. Пока приоритет применялся только к отобранным по времени, у человека с
 * убежавшими часами комната подменяла сессию, в которой он говорит, на соседнюю – или пропадала
 * вовсе, вместе с микрофоном и составом.
 *
 * Завершённая и отменённая не показываются никогда: ни завершение, ни отмена не двигают
 * `expiredAt`, поэтому у закрытой сессии время часто ещё идёт, и витрина, судящая по одним часам,
 * рисует ей LIVE.
 */
export const pickCurrentSession = <T extends CurrentSessionCandidate>(
  candidates: readonly T[],
  { now, voiceSessionId }: PickOptions
): T | null => {
  const live: T[] = [];
  const upcoming: T[] = [];
  let inVoice: T | null = null;

  for (const candidate of candidates) {
    if (isArchivedSessionStatus(candidate.status)) {
      continue;
    }

    if (voiceSessionId && candidate.sessionId === voiceSessionId) {
      inVoice = candidate;
      continue;
    }

    const start = timeOf(candidate.startedAt);
    const end = timeOf(candidate.expiredAt);

    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= now) {
      continue;
    }

    if (isLiveSession(candidate, now)) {
      live.push(candidate);
    } else if (start - now <= ROOM_APPEARS_BEFORE_START_MS) {
      upcoming.push(candidate);
    }
  }

  return inVoice ?? earliest(live) ?? earliest(upcoming);
};

/**
 * Покажет ли комната эту сессию прямо сейчас – тот же отбор, что внутри {@link pickCurrentSession}:
 * не в архиве, не закончилась и уже идёт либо стартует в окне `ROOM_APPEARS_BEFORE_START_MS`.
 *
 * Нужен окну сессии после «Вступить»: закрываться ему стоит только тогда, когда вместо него
 * появится комната. Завтрашняя сессия комнаты не получает, и закрытое окно оставляло человека с
 * одним тостом – без «что дальше».
 */
export const isRoomWindowOpen = (candidate: Omit<CurrentSessionCandidate, 'sessionId'>, now: number) => {
  if (isArchivedSessionStatus(candidate.status)) {
    return false;
  }
  const start = timeOf(candidate.startedAt);
  const end = timeOf(candidate.expiredAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= now) {
    return false;
  }
  return isLiveSession(candidate, now) || start - now <= ROOM_APPEARS_BEFORE_START_MS;
};

/**
 * Голос держит комнату на сессии, которой сервер уже не отдаёт.
 *
 * Сессия A доиграна, а я ещё в её голосе: с 23.09.2026 сервер не рвёт разговор вместе с
 * сессией, но строку A из отбора убирает. Выбери комната следующую сессию B - и меня слышно в
 * A, а её микрофона и выхода на экране нет. Поэтому, пока голос держит сессию, которую комната
 * показывала последней и которой нет в ответе, отбор не делаем, и хост остаётся на её прощании.
 */
export const isHeldByVoice = (
  voiceSessionId: string | null,
  lastShownSessionId: string | null,
  candidates: readonly CurrentSessionCandidate[]
): boolean =>
  voiceSessionId !== null &&
  lastShownSessionId === voiceSessionId &&
  !candidates.some((candidate) => candidate.sessionId === voiceSessionId);

/**
 * Сколько комната помнит сессию после того, как её время вышло.
 *
 * Память нужна ровно на прощание, и потолок обязан пережить его целиком (минута у «Сессия
 * закончилась»). Пять минут – с запасом на то, что вкладка провела конец сессии в фоне:
 * браузер придерживает таймеры, и человек возвращается к экрану уже после последнего тика.
 * Дальше плашке не место: вернувшемуся через час рассказывать про вечернюю сессию поздно.
 */
export const FAREWELL_MEMORY_MS = 5 * 60 * 1000;

/**
 * Сессия, которую комната показывала последней, – когда сервер её уже не отдаёт.
 *
 * Зачем: список комнаты приходит запросом `mySessions(archived: false)`, а он в SQL значит
 * `s.status IN ('UPCOMING','LIVE')` – закончившаяся сессия выпадает из ответа целиком, вместе со
 * своим статусом. Прощание же строится на разнице «была в отборе – пропала из него» и ищет
 * пропавшую строку в том же списке (`resolveFarewellPhase`). Пока памяти не было, искать было
 * негде: плашка «Сессия закончилась – чат остаётся» не показывалась ни разу, а комната просто
 * исчезала с экрана посреди игры. И даже когда сервер успевал отдать строку последний раз,
 * плашка обрывалась на середине: следующий рефетч уносил строку, и `farewellEntry` снова
 * становился пустым.
 *
 * Помним ровно одно – сессию, доигравшую у человека на глазах:
 *
 *  – строки у сервера нет, а её время вышло: это конец игры, и о нём надо сказать. Статус
 *    проставляем свой – сервер эту строку уже не отдаст, а сказать о ней надо;
 *  – строка пропала при живых часах: о себе она не сообщает ничего. Одинаково исчезают и
 *    отменённая сессия, и та, из которой я вышел сам, и та, откуда меня вывели, – а «Сессия
 *    закончилась» тому, кто только что нажал «Выйти», это ложь. Такую сессию комната отпускает
 *    молча, и для отмены это решение владельца от 07.09.2026 (см. `SessionRoomPhase`), а не
 *    пробел: читать в отменённой сессии нечего, тост «… отменил сессию» уже всё сказал.
 *
 * Ветки «сервер сказал, что строка закрыта» здесь больше нет. Она держала прощание отменённой
 * сессии и была недостижима с двух сторон: снимок берётся из `pickCurrentSession`, а тот
 * закрытые строки не отдаёт вовсе, да и сам запрос идёт с `archived: false` – закрытая строка
 * выпадает из ответа вместе со своим статусом. По той же причине молча уходит и сессия,
 * завершённая досрочно: её конец от моего выхода комнате отличить нечем. Чтобы плашка
 * доставалась и ей, комнате нужен источник состояния (архивный список или событие сокета) – это
 * в отчёте.
 *
 * Часы делают здесь ровно две вещи: отвечают, вышло ли время, и ограничивают память
 * `FAREWELL_MEMORY_MS` – прощание не должно всплывать через час.
 */
export const retainFarewellSession = <T extends CurrentSessionCandidate>(
  remembered: T | null | undefined,
  candidates: readonly T[],
  now: number
): T | null => {
  if (!remembered) {
    return null;
  }

  // Сервер всё ещё держит эту строку – она свежее любого снимка, и помнить нечего.
  if (candidates.some((candidate) => candidate.sessionId === remembered.sessionId)) {
    return null;
  }

  const end = timeOf(remembered.expiredAt);

  /*
    Давно ли всё это было. Сессия без времени окончания не помнится вовсе: о её конце сказать
    нечего – ни по стрелкам, ни как-либо ещё.
  */
  if (!Number.isFinite(end) || now - end > FAREWELL_MEMORY_MS) {
    return null;
  }

  return now >= end ? { ...remembered, status: 'COMPLETED' } : null;
};

/**
 * Ближайший момент, в который ответ `pickCurrentSession` может измениться сам собой: открытие
 * окна за 10 минут, старт и конец сессии.
 *
 * Нужен таймеру комнаты. Без него комната появляется и исчезает только по чужому событию –
 * человек, у которого сессия начинается через восемь минут, до перезагрузки страницы её не видел.
 */
export const nextSessionRoomBoundary = (
  candidates: readonly CurrentSessionCandidate[],
  now: number
): number | null => {
  let next: number | null = null;

  const consider = (moment: number) => {
    if (Number.isFinite(moment) && moment > now && (next === null || moment < next)) {
      next = moment;
    }
  };

  for (const candidate of candidates) {
    if (isArchivedSessionStatus(candidate.status)) {
      continue;
    }

    const start = timeOf(candidate.startedAt);
    consider(start - ROOM_APPEARS_BEFORE_START_MS);
    consider(start);
    consider(timeOf(candidate.expiredAt));
  }

  return next;
};
